import classNames from 'classnames';
import type { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import { useState } from 'react';
import type { CSSProperties } from 'styled-components';
import styled, { keyframes } from 'styled-components';

import { fadeIn, fadeOut } from '@sorare/core/src/atoms/animations/keyframes';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Video } from '@sorare/core/src/atoms/ui/Video';
import { useResultsStoryAssets } from '@sorare/core/src/components/animations/AnimatedStory/useResultsStoryAssets';
import {
  mediumMobileHeightAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';
import type { HexColor } from '@sorare/core/src/style/types';

import { DELAY_BEFORE_ANIMATION_MS } from '../CardLayout';
import { Cta } from './Cta';

const titleEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(30%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;

const Root = styled(Vertical).attrs({ gap: 0 })`
  isolation: isolate;
  width: 100%;
  height: 100%;
`;
const VideoPlayer = styled(Video)`
  position: absolute;
  inset: 0;
  z-index: -2;
`;
const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  background: var(--video-color);
  opacity: 0;

  mix-blend-mode: color;

  /* Target safari */
  @supports (mix-blend-mode: plus-darker) {
    mix-blend-mode: overlay;
  }

  animation: 250ms ${fadeIn} var(--video-color-delay) forwards;
`;
const Contents = styled(Vertical).attrs({ gap: 2, center: true })`
  width: 100%;
  height: 100%;
  max-height: 100vh;
  padding: var(--quadruple-unit) var(--unit) var(--triple-unit) var(--unit);
  justify-content: space-between;

  @media ${mediumMobileHeightAndAbove} {
    padding: calc(8 * var(--unit)) var(--unit);
  }

  &.isUnmounting {
    animation: ${fadeOut} 0.5s ease-out forwards;
  }
  &:not(.noScroll) {
    overflow-y: auto;
  }

  @media ${tabletAndAbove} {
    gap: var(--triple-unit);
  }
`;
const Buttons = styled(Vertical)`
  margin-top: auto;
  min-width: 200px;
  opacity: 0;
  animation: ${titleEnter} 0.5s ease-out calc(var(--animation-delay) + 1.8s)
    forwards;

  @media ${tabletAndAbove} {
    position: static;
    bottom: var(--quadruple-unit);
  }
`;
const FadeIn = styled(Horizontal).attrs({ center: true })`
  width: 100%;
  flex: 1;
  opacity: 0;
  animation: ${fadeIn} 1s ease-out var(--animation-delay) forwards;

  @media ${tabletAndAbove} {
    max-width: 80%;
  }
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: var(--double-unit);
  right: var(--double-unit);
`;
const Body = styled(Vertical)`
  position: relative;
  flex: 1;
`;
const TitleWrapper = styled.div`
  opacity: 0;
  animation: ${titleEnter} 0.5s ease-out var(--animation-delay) forwards;
`;

type Props = {
  title?: ReactNode;
  video: {
    asset: ComponentProps<typeof Video>['sources'];
    color?: HexColor;
    colorDelay?: number;
  };
  cta?: ReactNode;
  banner?: ReactNode;
  onNext?: () => void;
  onClose: () => Promise<void>;
  noScroll?: boolean;
};
export const LastFixtureStoryStep = ({
  title,
  children,
  video,
  cta,
  banner,
  onNext,
  onClose,
  noScroll,
}: PropsWithChildren<Props>) => {
  const [isUnmounting, setIsUnmounting] = useState(false);
  const { outro } = useResultsStoryAssets();

  const videoAsset = isUnmounting ? outro : video.asset;

  const actionAfterOutro = async (action?: () => void) =>
    new Promise<void>(resolve => {
      setIsUnmounting(true);
      setTimeout(async () => {
        await action?.();
        resolve();
      }, 2500);
    });

  return (
    <Root
      style={
        {
          '--animation-delay': `${DELAY_BEFORE_ANIMATION_MS}ms`,
        } as CSSProperties
      }
    >
      {banner}
      <Body>
        <CloseButtonWrapper className={classNames({ withBanner: !!banner })}>
          <CloseButton
            size="medium"
            onClose={async () => actionAfterOutro(onClose)}
          />
        </CloseButtonWrapper>
        {!!video && (
          <>
            {!!video.color && (
              <VideoOverlay
                style={
                  {
                    '--video-color': video.color,
                    '--video-color-delay': `${video.colorDelay || 0}ms`,
                  } as CSSProperties
                }
              />
            )}
            <VideoPlayer
              // remount video for the new one to play
              key={videoAsset[0]?.src}
              autoPlay
              muted
              playsInline
              sources={videoAsset}
              hideMutedIcon
            />
          </>
        )}
        <Contents className={classNames({ isUnmounting, noScroll })}>
          {!!title && <TitleWrapper>{title}</TitleWrapper>}
          <FadeIn>{children}</FadeIn>
          <Buttons>
            {cta || (
              <Cta
                onNext={onNext}
                onClose={async () => actionAfterOutro(onClose)}
              />
            )}
          </Buttons>
        </Contents>
      </Body>
    </Root>
  );
};
